import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { FileUpIcon, PencilLineIcon, ScanLineIcon } from 'lucide-react'
import { Button } from '@app/components/ui/button'
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@app/components/ui/card'
import { ErrorBlock, SkeletonBar, StatusBlock } from '@app/features/modeller/states'
import {
  fetchSayaModel,
  sayaKeys,
  type SayaModelPayload,
  type SayaRevisionSummary,
} from './api'
import { formatCount, formatDate, formatDesi, SOURCE_LABELS } from './format'

/**
 * The saya section of the model card — a stacked Card like every other section
 * on that page (colours, size distribution, recipe), not a tab.
 *
 * It is the module's only entrance by design: uploading a scan from the model
 * card is what makes mis-linking a set to the wrong model structurally
 * impossible, so there is no saya item in the sidebar and no unlinked-scan
 * queue to sort out later.
 *
 * It fetches on its own rather than riding the model payload: saya lives in the
 * `app` schema and Korgun's model card knows nothing about it, so a model with
 * no set costs one small 200 instead of widening every model response.
 */
export function SayaCard({ modelKod }: { modelKod: string }) {
  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: sayaKeys.model(modelKod),
    queryFn: () => fetchSayaModel(modelKod),
  })

  const revision = data ? (data.current ?? data.draft) : null

  return (
    <Card>
      <CardHeader>
        <CardTitle>Saya</CardTitle>
        <span className="text-micro text-ink-muted">
          Kalıp seti
        </span>
      </CardHeader>

      {isPending ? (
        <CardContent className="flex flex-col gap-2" aria-busy>
          <SkeletonBar className="w-44" />
          <SkeletonBar className="w-full" />
          <SkeletonBar className="w-2/3" />
        </CardContent>
      ) : null}

      {isError ? (
        <ErrorBlock
          message={error instanceof Error ? error.message : 'Saya bilgisi alınamadı.'}
          onRetry={() => void refetch()}
        />
      ) : null}

      {data && !revision ? (
        <StatusBlock
          title="Saya seti yok"
          description="Bu modelin saya kalıpları henüz dijitalleştirilmedi. Kalıpları tarayıp iz çıkarabilir ya da hazır bir DXF içe aktarabilirsiniz."
        />
      ) : null}

      {revision ? (
        <CardContent className="flex flex-col gap-3">
          <RevisionSummary revision={revision} />
          {data && data.revisions.length > 0 ? (
            <RevisionList revisions={data.revisions} />
          ) : null}
        </CardContent>
      ) : null}

      {data ? <SayaEntry data={data} modelKod={modelKod} /> : null}
    </Card>
  )
}

/**
 * The way in, labelled by what the click will actually do.
 *
 * All three states land on the same screen, so the label is the only thing that
 * can tell them apart: with a draft in progress the button resumes it (it
 * creates nothing), with only an approved set it opens a read-only revision,
 * and "Yeni saya seti" is kept strictly for the case where the click really
 * does create one. The DXF door is offered only where a DXF can still be
 * imported — a draft that already holds a scan has one source and cannot take a
 * second, so the button would open a screen with no drop zone on it.
 */
function SayaEntry({ data, modelKod }: { data: SayaModelPayload; modelKod: string }) {
  const draft = data.draft
  const canImportDxf = draft ? draft.scans.length === 0 : data.current === null

  return (
    <CardFooter className="justify-end gap-2">
      {canImportDxf ? (
        <Button asChild variant="outline" size="sm">
          <Link to="/modeller/$modelKod/saya" params={{ modelKod }} search={{ source: 'dxf' }}>
            <FileUpIcon aria-hidden />
            DXF içe aktar
          </Link>
        </Button>
      ) : null}
      <Button asChild variant="primary" size="sm">
        <Link to="/modeller/$modelKod/saya" params={{ modelKod }}>
          {draft ? <PencilLineIcon aria-hidden /> : <ScanLineIcon aria-hidden />}
          {draft || data.current ? 'Saya setini aç' : 'Yeni saya seti'}
        </Link>
      </Button>
    </CardFooter>
  )
}

function RevisionSummary({ revision }: { revision: SayaRevisionSummary }) {
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1.5">
        <Meta label="Ana beden" value={formatCount(revision.masterSize)} />
        <Meta label="Parça" value={formatCount(revision.partCount)} />
        <Meta label="Kaynak" value={SOURCE_LABELS[revision.source]} />
        <Meta label="Oluşturma" value={formatDate(revision.createdAt)} />
      </div>

      <div>
        <p className="eyebrow">Malzeme başına net desi — çift başına</p>
        {revision.netDesiPerPairByMaterial.length === 0 ? (
          <p className="mt-1 text-data text-ink-muted">
            Parçalara henüz malzeme atanmadı.
          </p>
        ) : (
          <ul className="mt-1 flex flex-col">
            {revision.netDesiPerPairByMaterial.map((row) => (
              <li
                key={row.materialId ?? 'atanmamis'}
                className="flex items-baseline justify-between gap-3 border-b border-hairline py-1 last:border-b-0"
              >
                <span className="min-w-0 truncate text-data text-ink">
                  {row.materialName ?? 'Malzeme atanmamış'}
                </span>
                <span className="shrink-0 font-mono text-data tnum text-ink">
                  {formatDesi(row.netDesi)}
                  <span className="ml-1 font-sans text-micro text-ink-muted">desi</span>
                </span>
              </li>
            ))}
          </ul>
        )}
        {/*
         * Net is the sum of the pieces themselves. What a hide actually gives
         * up — placement waste, fire, price — is a later slice, and calling
         * this "tüketim" before that exists would be a lie with a decimal
         * point on it.
         */}
        <p className="mt-1.5 text-micro text-ink-muted">
          Net alan; yerleşim firesi ve maliyet bu dilime dahil değil.
        </p>
      </div>
    </div>
  )
}

/** Newest first, capped: the history is context, not the point of the card. */
const VISIBLE_REVISION_COUNT = 6

function RevisionList({ revisions }: { revisions: readonly SayaRevisionSummary[] }) {
  const visible = revisions.slice(0, VISIBLE_REVISION_COUNT)
  const hidden = revisions.length - visible.length

  return (
    <div>
      <p className="eyebrow">Geçmiş</p>
      <ul className="mt-1 flex flex-wrap gap-1">
        {visible.map((revision) => (
          <li
            key={revision.id}
            className="inline-flex items-center gap-1.5 rounded-xs border border-hairline bg-kagit px-1.5 py-0.5"
            title={`${revision.createdBy} oluşturdu`}
          >
            <span className="font-mono text-micro tnum text-ink-muted">
              {formatDate(revision.createdAt)}
            </span>
          </li>
        ))}
        {hidden > 0 ? (
          <li className="self-center text-micro text-ink-muted">
            +{formatCount(hidden)} daha eski
          </li>
        ) : null}
      </ul>
    </div>
  )
}

function Meta({ label, value }: { label: string; value: string }) {
  return (
    <span className="flex items-baseline gap-1.5">
      <span className="eyebrow">{label}</span>
      <span className="font-mono text-data tnum text-ink">{value}</span>
    </span>
  )
}
